import React, { useRef, useState, useEffect } from "react";
import { Form, Row, Col, Button } from "react-bootstrap";
import { FaChevronDown } from "react-icons/fa";
import { getUiInterface } from "../../../locales/translationUtils";
import { useGeneric } from "../../../apis/generic/GenericProvider";
import getFilterLabels from "../../../types/filterLabels";
import { useTranslation } from 'react-i18next';

interface FilterProps {
  filters: Record<string, string>;
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSearch: () => void;
}

const Filter: React.FC<FilterProps> = ({ filters, onFilterChange, onSearch }) => {
  const { t } = useTranslation();
  const filterLabels = getFilterLabels();
  const selectRefs = useRef<(HTMLSelectElement | null)[]>([]);
  const [customPeriodFields, setCustomPeriodFields] = useState<Record<string, boolean>>({});
  const { distinctOptions, fetchDistinctOptions } = useGeneric();

  // Campos que são datas e precisam de seleção de período (case-insensitive)
  const dateFieldsLower = ['arrivaldate', 'pu'];
  const isDateField = (key: string) => dateFieldsLower.includes(key.toLowerCase());
  const dateFieldOptions = [
    t('release.fields.last7Days'),
    t('release.fields.last15Days'),
    t('release.fields.last30Days'),
    t('release.fields.customPeriod')
  ];

  const selectOptions: Record<string, any[]> = {
    from: distinctOptions["wareHouse"] || [],
  };
  
  // Adicionar opções de período para campos de data dinamicamente
  Object.keys(filters).forEach(key => {
    if (isDateField(key)) {
      selectOptions[key] = dateFieldOptions;
    }
  });

  useEffect(() => {
    fetchDistinctOptions('wareHouse');
  }, [fetchDistinctOptions]);

  const handleSelectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    onFilterChange(e);

    // Verificar se é um campo de data
    if (isDateField(name)) {
      setCustomPeriodFields(prev => ({ ...prev, [name]: value === t('release.fields.customPeriod') }));
      updateDateRange(name, value);
    }
  };

  const updateDateRange = (fieldName: string, selectedPeriod: string) => {
    let startDate = "";
    let endDate = new Date().toISOString().split("T")[0];

    switch (selectedPeriod) {
      case t('release.fields.last7Days'):
        startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        break;
      case t('release.fields.last15Days'):
        startDate = new Date(Date.now() - 15 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        break;
      case t('release.fields.last30Days'):
        startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split("T")[0];
        break;
      case t('release.fields.customPeriod'):
        return;
      default:
        return;
    }

    // Gera keys para início/fim mantendo o case original do campo
    const inicioKey = fieldName.toLowerCase() === 'arrivaldate' ? 'arrivalDateInicio' : `${fieldName}Inicio`;
    const fimKey = fieldName.toLowerCase() === 'arrivaldate' ? 'arrivalDateFim' : `${fieldName}Fim`;

    onFilterChange({
      target: { name: inicioKey, value: startDate },
    } as React.ChangeEvent<HTMLInputElement>);

    onFilterChange({
      target: { name: fimKey, value: endDate },
    } as React.ChangeEvent<HTMLInputElement>);
  };
  
  // Campos ocultos (períodos de data, paginação e ordenação) - case-insensitive
  const hiddenFieldsLower = ['arrivaldateinicio', 'arrivaldatefim', 'puinicio', 'pufim', 'pagenum', 'quantityperpage', 'sort', 'order'];
  const isHiddenField = (key: string) => hiddenFieldsLower.includes(key.toLowerCase());

  return (
    <Form className="mb-2">
      <Row className="mb-3">
        {Object.entries(filters)
          .filter(([key]) => !isHiddenField(key))
          .map(([key, value], index) => (
            <Col md={2} key={key}>
              <Form.Group controlId={`filter-${key}`} className="position-relative">
                <Form.Label>{filterLabels[key] || key.charAt(0).toUpperCase() + key.slice(1)}</Form.Label>

                {selectOptions[key as keyof typeof selectOptions] ? (
                  <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                    <Form.Control
                      as="select"
                      name={key}
                      value={value}
                      onChange={handleSelectChange}
                      ref={(el: HTMLSelectElement | null) => {
                        if (el) selectRefs.current[index] = el;
                      }}
                      style={{ appearance: "none", paddingRight: "30px", cursor: "pointer" }}
                    >
                      <option value="">{t(`release.labels.select${key.charAt(0).toUpperCase() + key.slice(1)}`)}</option>
                      {selectOptions[key as keyof typeof selectOptions]?.map((option) => {
                        if (typeof option === "object" && "id" in option && "description" in option) {
                          return (
                            <option key={option.id} value={option.id}>
                              {option.description}
                            </option>
                          );
                        }
                        return (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        );
                      })}
                    </Form.Control>
                    <FaChevronDown
                      style={{
                        position: "absolute",
                        right: "10px",
                        pointerEvents: "auto",
                        cursor: "pointer",
                      }}
                      onClick={() => selectRefs.current[index]?.focus()}
                    />
                  </div>
                ) : (
                  <Form.Control
                    type="text"
                    placeholder={key}
                    name={key}
                    value={value}
                    onChange={onFilterChange}
                  />
                )}
              </Form.Group>
            </Col>
          ))}
      </Row>

      <Row>
        {/* Campos de período customizado para datas */}
        {Object.keys(filters).filter(isDateField).map(field => {
          if (!customPeriodFields[field]) return null;
          const inicioKey = field.toLowerCase() === 'arrivaldate' ? 'arrivalDateInicio' : `${field}Inicio`;
          const fimKey = field.toLowerCase() === 'arrivaldate' ? 'arrivalDateFim' : `${field}Fim`;
          return (
            <React.Fragment key={`${field}-custom`}>
              <Col md={2}>
                <Form.Group controlId={`${field}Inicio`}>
                  <Form.Label>{filterLabels[field] || filterLabels[field.toLowerCase()] || field} {t('release.fields.start')}</Form.Label>
                  <Form.Control
                    type="date"
                    name={inicioKey}
                    value={filters[inicioKey] || ""}
                    onChange={onFilterChange}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group controlId={`${field}Fim`}>
                  <Form.Label>{filterLabels[field] || filterLabels[field.toLowerCase()] || field} {t('release.fields.end')}</Form.Label>
                  <Form.Control
                    type="date"
                    name={fimKey}
                    value={filters[fimKey] || ""}
                    onChange={onFilterChange}
                  />
                </Form.Group>
              </Col>
            </React.Fragment>
          );
        })}
      </Row>

      <Row>
        <Col md={4}>
          <Button variant="primary" onClick={onSearch}>
            {getUiInterface("search")}
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export default Filter;